import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

// ─────────────────────────────────────────────────────────────
// Types (matching backend PI-SPI schema)
// ─────────────────────────────────────────────────────────────

export interface PispiAliasClient {
  nom: string;
  paysResidence: string;
  ville?: string;
  categorie?: string;
}

export interface PispiAliasInfo {
  cle: string;
}

export interface PispiAliasLookupResponse {
  client: PispiAliasClient;
  alias: PispiAliasInfo;
}

export interface PispiRtpInput {
  invoiceId: string;
  payeurAlias: string;
  dateLimitePaiement: string;
  motif?: string;
  confirmation?: boolean;
}

export interface PispiRtpResponse {
  txId: string;
  endToEndId?: string;
  statut: string;
  statutRaison?: string;
  montant?: number | string;
}

/** GET /api/pispi/config — the secrets themselves are never returned. */
export interface PispiConfig {
  tokenUrl: string;
  apiBase: string;
  clientId: string;
  accountNumero: string;
  aliasUuid: string | null;
  enabled: boolean;
  clientSecretSet: boolean;
  apiKeySet: boolean;
  scopePrefix: string;
  updatedAt: string;
}

/** PUT /api/pispi/config. An empty secret keeps the stored one. */
export interface PispiConfigInput {
  tokenUrl: string;
  apiBase: string;
  clientId: string;
  clientSecret?: string;
  apiKey?: string;
  accountNumero: string;
  aliasUuid: string | null;
  enabled: boolean;
}

/** POST /api/pispi/config/test. */
export interface PispiConnectionTest {
  ok: boolean;
  message: string;
  accounts?: number;
  accountFound?: boolean;
}

/** GET /api/pispi/webhook-subscription — callback URL token masked. */
export interface PispiWebhookSubscription {
  id: string;
  pispiWebhookId: string;
  events: string[];
  callbackUrl: string;
  dateCreation: string;
}

/** GET /api/pispi/invoices/:id/payment-link. */
export interface PispiPaymentLink {
  url: string;
  expiresAt: string;
}

/** Un statut d'événement PAIEMENT_RECU : GET /api/pispi/events?status=. */
export type PispiEventStatus = 'unprocessed' | 'terminal';

/** Une ligne de `packages/api/src/routes/pispi/index.ts` GET /events. */
export interface PispiEvent {
  id: string;
  endToEndId: string | null;
  txId: string | null;
  processingError: string | null;
  terminalFailure: boolean;
  attempts: number;
  nextAttemptAt: string | null;
  receivedAt: string;
  /** False while the automatic retry still owns the row: resolve would 409. */
  resolvable: boolean;
}

export interface PispiEventsResponse {
  events: PispiEvent[];
}

export interface ResolvePispiEventInput {
  id: string;
  note?: string | undefined;
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const pispiKeys = {
  all: ['pispi'] as const,
  aliasLookup: (cle: string) => [...pispiKeys.all, 'alias-lookup', cle] as const,
  events: (status: PispiEventStatus) => [...pispiKeys.all, 'events', status] as const,
  paymentLink: (invoiceId: string) => [...pispiKeys.all, 'payment-link', invoiceId] as const,
  config: () => [...pispiKeys.all, 'config'] as const,
  webhooks: () => [...pispiKeys.all, 'webhook-subscription'] as const,
};

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function lookupAlias(cle: string): Promise<PispiAliasLookupResponse> {
  return apiClient.get<PispiAliasLookupResponse>('/pispi/alias-lookup', { cle });
}

async function sendRtp(input: PispiRtpInput): Promise<PispiRtpResponse> {
  return apiClient.post<PispiRtpResponse>('/pispi/rtp', input);
}

async function fetchPispiConfig(): Promise<{ config: PispiConfig | null }> {
  return apiClient.get<{ config: PispiConfig | null }>('/pispi/config');
}

async function savePispiConfig(input: PispiConfigInput): Promise<{ config: PispiConfig | null }> {
  return apiClient.put<{ config: PispiConfig | null }>('/pispi/config', input);
}

async function testPispiConnection(): Promise<PispiConnectionTest> {
  return apiClient.post<PispiConnectionTest>('/pispi/config/test', {}, { noQueue: true });
}

async function fetchPispiWebhooks(): Promise<{ subscriptions: PispiWebhookSubscription[] }> {
  return apiClient.get<{ subscriptions: PispiWebhookSubscription[] }>('/pispi/webhook-subscription');
}

async function createPispiWebhook(): Promise<{ subscription: PispiWebhookSubscription }> {
  return apiClient.post<{ subscription: PispiWebhookSubscription }>('/pispi/webhook-subscription', {}, { noQueue: true });
}

async function deletePispiWebhook(id: string): Promise<void> {
  await apiClient.delete(`/pispi/webhook-subscription/${encodeURIComponent(id)}`);
}

async function fetchPaymentLink(invoiceId: string): Promise<PispiPaymentLink> {
  return apiClient.get<PispiPaymentLink>(`/pispi/invoices/${encodeURIComponent(invoiceId)}/payment-link`);
}

async function fetchPispiEvents(status: PispiEventStatus): Promise<PispiEventsResponse> {
  return apiClient.get<PispiEventsResponse>('/pispi/events', { status });
}

async function resolvePispiEvent(input: ResolvePispiEventInput): Promise<{ resolved: true }> {
  return apiClient.post<{ resolved: true }>(`/pispi/events/${input.id}/resolve`, {
    note: input.note,
  });
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Hook to look up a PI-SPI alias by its UUID key.
 * GET /api/pispi/alias-lookup?cle=<uuid>
 *
 * Only runs when `cle` is a non-empty string. Callers should gate
 * `enabled` on client-side UUID validation to avoid 400 responses.
 */
export function usePispiAliasLookup(cle: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pispiKeys.aliasLookup(cle),
    queryFn: () => lookupAlias(cle),
    enabled: (options?.enabled ?? true) && !!cle,
    retry: false,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Public « Payer en ligne » link of an invoice, fetched only while the
 * dialog showing it is open. GET /api/pispi/invoices/:id/payment-link.
 */
export function usePispiPaymentLink(invoiceId: string, options: { enabled: boolean }) {
  return useQuery({
    queryKey: pispiKeys.paymentLink(invoiceId),
    queryFn: () => fetchPaymentLink(invoiceId),
    enabled: options.enabled && !!invoiceId,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * PAIEMENT_RECU events that could not auto-process (missing endToEndId,
 * ambiguous établissement). GET /api/pispi/events?status=. `unprocessed`
 * is still under automatic retry; `terminal` gave up and needs a manual
 * reconciliation.
 */
export function usePispiEvents(status: PispiEventStatus) {
  return useQuery({
    queryKey: pispiKeys.events(status),
    queryFn: () => fetchPispiEvents(status),
    staleTime: 1000 * 30,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/**
 * Hook to send a PI-SPI Request-To-Pay (RTP).
 * POST /api/pispi/rtp
 */
export function useSendPispiRtp() {
  return useMutation({
    mutationFn: (input: PispiRtpInput) => sendRtp(input),
  });
}

/**
 * Manually closes out a PAIEMENT_RECU event (POST
 * /api/pispi/events/:id/resolve). Never credits a payment — only marks the
 * row as processed once an operator has reconciled it another way.
 * Invalidates both lists: depending on `terminalFailure`, the resolved row
 * came from either one.
 */
export function useResolvePispiEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ResolvePispiEventInput) => resolvePispiEvent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pispiKeys.all });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Tenant configuration (Paramètres › PI-SPI)
// ─────────────────────────────────────────────────────────────

export function usePispiConfig() {
  return useQuery({ queryKey: pispiKeys.config(), queryFn: fetchPispiConfig, retry: false });
}

export function useSavePispiConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: savePispiConfig,
    onSuccess: (data) => {
      queryClient.setQueryData(pispiKeys.config(), data);
    },
  });
}

export function useTestPispiConnection() {
  return useMutation({ mutationFn: testPispiConnection });
}

export function usePispiWebhooks(options: { enabled: boolean }) {
  return useQuery({
    queryKey: pispiKeys.webhooks(),
    queryFn: fetchPispiWebhooks,
    enabled: options.enabled,
    retry: false,
  });
}

export function useCreatePispiWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPispiWebhook,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pispiKeys.webhooks() });
    },
  });
}

export function useDeletePispiWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePispiWebhook,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pispiKeys.webhooks() });
    },
  });
}
